import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileCode, FolderOpen, Play, Plus, RefreshCw, Stethoscope, Unlink } from 'lucide-react';
import { ConfirmModal } from '../../ui/Modal';
import Tooltip from '../../ui/Tooltip';
import RunConfigEditorModal from './RunConfigEditorModal';
import { freePort, loadRunConfigs, portOverride, saveRunConfigs } from './runApi';
import { approve, commandLine, isApproved } from './runApproval';
import { liveKeySet, runKey, selectLiveKeys, startRun, stopRunsUnder, useRunStore } from './runStore';
import { errorText } from './runErrors';
import LinkLocalClone from './LinkLocalClone';
import { clonePathFor, localPathOf } from '../../../services/git/codeRepoClones';
import { unlinkClone } from './localClone';
import RunRow, { IconButton } from './RunRow';
import OpenInTerminalButton from './OpenInTerminalButton';
import RunDoctorPanel, { DoctorConfirm } from './doctor/RunDoctorPanel';
import RunFileEditor, { LAUNCH_FILE } from './doctor/RunFileEditor';
import { useRepoDoctor } from './doctor/useRepoDoctor';
import { blockingFor, safeSteps } from './doctor/doctorModel';
import { patchDoctor, runCheck } from './doctor/doctorStore';
import './ProjectRun.css';
import './doctor/RunDoctor.css';

export { errorText };

function sourceLabel(t, data) {
  if (!data) return '';
  if (data.source === 'apiboo') return '.apiboo/launch.json';
  if (data.source === 'claude') return t('projects.run.fromClaude');
  if (data.source === 'suggested') return t('projects.run.suggested');
  return '';
}

/** The configurations of one local repo. Runs go through the section's `onRequestRun` (approval). */
function RepoRunBlock({ repo, onRequestRun, onConfigs, onUnlink, projectName }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editor, setEditor] = useState(null); // { initial } | null
  const [deleting, setDeleting] = useState(null);
  const [openLogs, setOpenLogs] = useState(() => new Set());
  const [actionError, setActionError] = useState(null);
  const [fileEditor, setFileEditor] = useState(null); // repo-relative file open in the editor modal
  const liveJoined = useRunStore(selectLiveKeys);
  const liveKeys = useMemo(() => liveKeySet(liveJoined), [liveJoined]);

  // Only the newest load may land: a Reload click and a save can overlap.
  const loadSeq = useRef(0);
  const reload = useCallback(() => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    loadRunConfigs(repo.path)
      .then((res) => {
        if (seq !== loadSeq.current) return;
        setData(res);
        setLoadError(null);
      })
      .catch((e) => {
        if (seq !== loadSeq.current) return;
        // Nothing stale stays runnable next to the error (a moved folder).
        setData(null);
        setLoadError(String(e?.message || e));
      });
  }, [repo.path]);
  useEffect(() => {
    reload();
    return () => {
      loadSeq.current += 1; // unmounted / repo changed: ignore what is in flight
    };
  }, [reload]);

  const configs = useMemo(() => data?.configurations || [], [data]);
  useEffect(() => onConfigs(repo.path, configs), [onConfigs, repo.path, configs]);
  const fileIsOurs = data?.source === 'apiboo';
  const fileBroken = !!data?.error;
  // Check setup: checked when the configurations load; fixes run on a click.
  const d = useRepoDoctor(repo, configs);
  const { envOpened, clearEnvOpened } = d;
  useEffect(() => {
    // "Create .env from example" → the new file opens in the editor
    if (envOpened) {
      setFileEditor(envOpened);
      clearEnvOpened();
    }
  }, [envOpened, clearEnvOpened]);

  const persist = async (next) => {
    await saveRunConfigs(repo.path, next);
    reload();
  };

  const requestRun = (list, opts) => {
    setActionError(null);
    onRequestRun(
      list.map((config) => ({ repo, config })),
      opts
    );
  };

  // Port taken: the first free port above it, the configuration changed to
  // listen there (Vite --port, Next -p, CRA PORT …) for this run only.
  // Approval is the configuration's own: the port flag is ours, not the repo's.
  const switchPort = async (config) => {
    setActionError(null);
    try {
      const busy = useRunStore.getState().runs[runKey(repo.path, config.name)]?.port || config.port;
      const port = await freePort(busy + 1);
      const { configuration } = await portOverride(repo.path, config, port);
      onRequestRun([{ repo, config: configuration, base: config }], { skipDoctor: true });
    } catch (e) {
      setActionError(errorText(t, String(e?.message || e)));
    }
  };

  const submitEditor = async (config) => {
    const original = editor?.initial?.name;
    const next = original ? configs.map((c) => (c.name === original ? config : c)) : [...configs, config];
    await persist(next);
    setEditor(null);
  };

  const confirmDelete = () => {
    const name = deleting?.name;
    setDeleting(null);
    persist(configs.filter((c) => c.name !== name)).catch((e) => setActionError(t('projects.run.saveFailed', { error: String(e?.message || e) })));
  };

  // "Create .apiboo/launch.json": the suggestions (or an empty list) saved, then opened in the editor.
  const saveAsOurs = () => {
    setActionError(null);
    persist(configs)
      .then(() => setFileEditor(LAUNCH_FILE))
      .catch((e) => setActionError(t('projects.run.saveFailed', { error: String(e?.message || e) })));
  };

  /** A checklist / banner fix: an action handled here, or commands through the doctor. */
  const onFix = (item, opts) => {
    const fix = item.fix;
    if (!fix) return;
    if (fix.action === 'open_file') setFileEditor(fix.file);
    else if (fix.action === 'use_port') {
      const config = configs.find((c) => item.configs.includes(c.name));
      if (config) switchPort(config);
    } else d.requestFix([item], opts);
  };
  // Banner on a failed row: install into THAT row's log; "& run" only after it succeeded.
  const installFromRow = (config) => (item, andRun) =>
    onFix(item, {
      keyFor: () => runKey(repo.path, config.name),
      after: (ok) => ok && andRun && requestRun([config], { skipDoctor: true }),
    });
  const runAllFromDoctor = () => {
    const pending = d.doctor.pending;
    patchDoctor(repo.path, { pending: null, offerRun: false, open: false });
    if (pending) onRequestRun(pending, { skipDoctor: true });
    else requestRun(configs.filter((c) => !liveKeys.has(runKey(repo.path, c.name))));
  };
  const startAnyway = () => {
    const pending = d.doctor.pending;
    patchDoctor(repo.path, { pending: null });
    if (pending) onRequestRun(pending, { skipDoctor: true });
  };
  const safe = safeSteps(d.doctor.report);

  const toggleLogs = (name) =>
    setOpenLogs((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const notRunning = configs.filter((c) => !liveKeys.has(runKey(repo.path, c.name)));
  // Runs are keyed by folder: forgetting it stops them first (nothing may
  // keep running with no row left to stop it).
  const anyLive = [...liveKeys].some((k) => k.startsWith(runKey(repo.path, '')));

  return (
    <div className="pj-run-repo" data-testid="run-repo">
      <div className="pj-run-repo-head">
        <span className="pj-run-repo-name">{repo.name || repo.path}</span>
        {repo.deviceClone && (
          <Tooltip content={repo.path} side="bottom">
            <span className="pj-run-source pj-run-clone-path" data-testid="run-clone-path">
              <FolderOpen size={12} aria-hidden /> {repo.path}
            </span>
          </Tooltip>
        )}
        {sourceLabel(t, data) &&
          (fileIsOurs ? (
            <button
              type="button"
              className="pj-run-source pj-run-source--link"
              data-testid="run-source"
              onClick={() => setFileEditor(LAUNCH_FILE)}
              title={t('projects.run.doctor.openLaunch')}
            >
              <FileCode size={12} aria-hidden /> {sourceLabel(t, data)}
            </button>
          ) : (
            <span className="pj-run-source" data-testid="run-source">
              <FileCode size={12} aria-hidden /> {sourceLabel(t, data)}
            </span>
          ))}
        <span className="pj-run-repo-actions">
          {safe.length > 0 && (
            <button type="button" className="gd-btn" onClick={d.fixAll} disabled={!!d.running} data-testid="run-install-all">
              <Download size={13} /> {t('projects.run.doctor.installAll')}
            </button>
          )}
          {configs.length > 1 && notRunning.length > 0 && (
            <button type="button" className="gd-btn" onClick={() => requestRun(notRunning)} data-testid="run-all">
              <Play size={13} /> {t('projects.run.runAll')}
            </button>
          )}
          {configs.length > 0 && (
            <IconButton
              label={t('projects.run.doctor.check')}
              onClick={() => {
                patchDoctor(repo.path, { open: true });
                d.recheck({ force: true });
              }}
              testId="run-doctor-check"
            >
              <Stethoscope size={13} />
            </IconButton>
          )}
          <OpenInTerminalButton repoPath={repo.path} projectName={projectName} testId="run-open-terminal" />
          {data && !fileIsOurs && !fileBroken && configs.length > 0 && (
            <button type="button" className="gd-btn" onClick={saveAsOurs} data-testid="run-save-file">
              <FileCode size={13} /> {t('projects.run.createFile')}
            </button>
          )}
          {data && !fileBroken && (
            <IconButton label={t('projects.run.add')} onClick={() => setEditor({ initial: null })} testId="run-add">
              <Plus size={14} />
            </IconButton>
          )}
          <IconButton label={t('projects.run.reload')} onClick={reload} testId="run-reload">
            <RefreshCw size={13} />
          </IconButton>
          {repo.deviceClone && onUnlink && (
            <IconButton
              label={anyLive ? t('projects.run.clone.unlinkStop') : t('projects.run.clone.unlink')}
              onClick={() => onUnlink(repo)}
              testId="run-unlink-clone"
            >
              <Unlink size={13} />
            </IconButton>
          )}
        </span>
      </div>

      {loadError && (
        <div className="graph-error" role="alert">
          {errorText(t, loadError)}
        </div>
      )}
      {data?.error && (
        <div className="graph-error" role="alert" data-testid="run-file-error">
          {t('projects.run.fileError', { file: data.file, error: errorText(t, data.error) })}
          {data.file === LAUNCH_FILE && (
            <button type="button" className="gd-btn pj-run-fix-file" onClick={() => setFileEditor(LAUNCH_FILE)} data-testid="run-fix-file">
              <FileCode size={13} /> {t('projects.run.doctor.openLaunch')}
            </button>
          )}
        </div>
      )}
      {d.doctor.open && configs.length > 0 && (
        <RunDoctorPanel
          repo={repo}
          d={d}
          liveNames={new Set(configs.filter((c) => liveKeys.has(runKey(repo.path, c.name))).map((c) => c.name))}
          onFix={onFix}
          onRunAll={runAllFromDoctor}
          onStartAnyway={startAnyway}
          onClose={() => patchDoctor(repo.path, { open: false, pending: null })}
        />
      )}
      {actionError && (
        <div className="graph-error" role="alert">
          {actionError}
        </div>
      )}
      {data && !data.error && configs.length === 0 && (
        <p className="pj-result-meta pj-run-empty">
          {t('projects.run.emptyHint')}{' '}
          {data.source === 'none' && (
            <button type="button" className="gd-btn" onClick={saveAsOurs} data-testid="run-create-launch">
              <FileCode size={13} /> {t('projects.run.createFile')}
            </button>
          )}
        </p>
      )}
      {data?.source === 'suggested' && <p className="pj-result-meta pj-run-note">{t('projects.run.suggestedHint')}</p>}
      {data?.source === 'claude' && <p className="pj-result-meta pj-run-note">{t('projects.run.claudeHint')}</p>}
      {data?.skipped?.length > 0 && (
        <p className="pj-result-meta pj-run-note" data-testid="run-skipped">
          {t('projects.run.skipped', {
            count: data.skipped.length,
            list: data.skipped.map((s) => `${s.name || '?'} (${s.reason})`).join(', '),
          })}
        </p>
      )}

      {configs.length > 0 && (
        <ul className="pj-list pj-run-list">
          {configs.map((c) => (
            <RunRow
              key={c.name}
              repo={repo}
              config={c}
              logsOpen={openLogs.has(c.name)}
              onToggleLogs={() => toggleLogs(c.name)}
              onRun={requestRun}
              onUseAnotherPort={switchPort}
              onEdit={() => setEditor({ initial: c })}
              onDelete={() => setDeleting(c)}
              readOnlyFile={!fileIsOurs}
              report={d.doctor.report}
              onInstall={installFromRow(c)}
              onCheck={() => {
                patchDoctor(repo.path, { open: true });
                d.recheck({ force: true });
              }}
              onOpenFile={setFileEditor}
              onFailed={() => d.recheck()}
              projectName={projectName}
            />
          ))}
        </ul>
      )}

      {editor && (
        <RunConfigEditorModal
          initial={editor.initial}
          takenNames={configs.map((c) => c.name).filter((n) => n !== editor.initial?.name)}
          onSubmit={submitEditor}
          onClose={() => setEditor(null)}
        />
      )}
      <DoctorConfirm repo={repo} d={d} />
      {fileEditor && (
        <RunFileEditor
          repoPath={repo.path}
          rel={fileEditor}
          onClose={() => setFileEditor(null)}
          onSaved={() => {
            // launch.json: the configurations reload (a changed command asks again on its next run)
            if (fileEditor === LAUNCH_FILE) reload();
            d.recheck();
          }}
        />
      )}
      <ConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        type="danger"
        title={t('projects.run.deleteTitle')}
        message={t('projects.run.deleteConfirm', { name: deleting?.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
      />
    </div>
  );
}

/**
 * "Run" on a project page (Run & Share, Phase 1): the dev servers of the
 * project's linked LOCAL repos, with start/stop, logs, port readiness and a
 * preview window. Local only; a repo without a local folder (every team
 * project's repo) shows how to make it runnable instead.
 */
export default function ProjectRunSection({ repos, projectName = '' }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(null); // [{ repo, config }] awaiting "Run this?"
  const [configsByRepo, setConfigsByRepo] = useState({});
  const liveJoined = useRunStore(selectLiveKeys);
  const liveKeys = useMemo(() => liveKeySet(liveJoined), [liveJoined]);
  const onConfigs = useCallback(
    (path, configs) => setConfigsByRepo((prev) => (prev[path] === configs ? prev : { ...prev, [path]: configs })),
    []
  );
  // A repo linked by URL gets its folder on THIS device from codeRepoClones
  // (picked or cloned once, shared with the commit ↔ task links). Bumped on
  // link / unlink so the configurations show right away.
  const [cloneRev, setCloneRev] = useState(0);
  const withPaths = useMemo(
    () =>
      (repos || []).map((r) => {
        if (r.path) return r;
        const path = localPathOf(r);
        return path ? { ...r, path, deviceClone: true } : r;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cloneRev: the device store changed
    [repos, cloneRev]
  );
  const onLinked = useCallback(() => setCloneRev((n) => n + 1), []);
  const onUnlink = useCallback(async (repo) => {
    await stopRunsUnder(repo.path);
    if (clonePathFor(repo.id)) unlinkClone(repo);
    setCloneRev((n) => n + 1);
  }, []);
  if (!repos || repos.length === 0) return null;
  const local = withPaths.filter((r) => r.path);
  const remote = withPaths.filter((r) => !r.path);
  const linkable = remote.filter((r) => r.remote && r.id);
  const unlinkable = remote.filter((r) => !(r.remote && r.id));

  const notLive = (items) => items.filter(({ repo, config }) => !liveKeys.has(runKey(repo.path, config.name)));
  /** Start these, asking once for every command not approved yet (one dialog, across repos). */
  const requestRun = async (items, { skipDoctor = false } = {}) => {
    let toStart = notLive(items);
    if (toStart.length === 0) return;
    if (!skipDoctor) toStart = await preflight(toStart);
    if (toStart.length === 0) return;
    // `base`: a run on another port is approved as the configuration it came from.
    const approved = await Promise.all(toStart.map(({ repo, config, base }) => isApproved(repo.path, base || config)));
    if (approved.every(Boolean)) toStart.forEach(({ repo, config }) => startRun(repo.path, config));
    else setPending(toStart);
  };
  /**
   * Check setup before a start (cached, cheap): a repo with problems that
   * concern the configurations about to start is held back — its checklist
   * opens with "Start anyway" — and the others start. A check that cannot
   * run (browser, error) holds nothing back.
   */
  const preflight = async (toStart) => {
    const byRepo = new Map();
    toStart.forEach((it) => byRepo.set(it.repo.path, [...(byRepo.get(it.repo.path) || []), it]));
    const held = new Set();
    await Promise.all(
      [...byRepo.entries()].map(async ([path, list]) => {
        const all = configsByRepo[path]?.length ? configsByRepo[path] : list.map((i) => i.base || i.config);
        const report = await runCheck(path, all);
        if (blockingFor(report, list.map((i) => (i.base || i.config).name)).length) {
          held.add(path);
          patchDoctor(path, { open: true, pending: list, offerRun: false });
        }
      })
    );
    return toStart.filter((it) => !held.has(it.repo.path));
  };
  const confirmRun = async () => {
    const list = pending || [];
    setPending(null);
    await Promise.all(list.map(({ repo, config, base }) => approve(repo.path, base || config)));
    list.forEach(({ repo, config }) => startRun(repo.path, config));
  };
  // Every configuration of every local repo: "Run everything" when the project has several.
  const everything = notLive(local.flatMap((repo) => (configsByRepo[repo.path] || []).map((config) => ({ repo, config }))));
  const repoNames = new Set((pending || []).map(({ repo }) => repo.path));

  return (
    <section className="pj-group pj-run" data-testid="project-run">
      <h3 className="pj-group-head pj-run-head">
        {t('projects.run.title')}
        {local.length > 1 && everything.length > 0 && (
          <button type="button" className="gd-btn pj-run-everything" onClick={() => requestRun(everything)} data-testid="run-everything">
            <Play size={13} /> {t('projects.run.runEverything', { count: local.length })}
          </button>
        )}
      </h3>
      {local.map((r) => (
        <RepoRunBlock key={`${r.id || ''}|${r.path}`} repo={r} onRequestRun={requestRun} onConfigs={onConfigs} onUnlink={onUnlink} projectName={projectName} />
      ))}
      {linkable.map((r) => (
        <LinkLocalClone key={r.id} repo={r} onLinked={onLinked} />
      ))}
      {unlinkable.length > 0 && (
        <p className="pj-result-meta pj-run-note" data-testid="run-link-local-hint">
          {t('projects.run.linkLocalHint', { names: unlinkable.map((r) => r.name || r.host || '?').join(', ') })}
        </p>
      )}
      <ConfirmModal
        isOpen={!!pending}
        onClose={() => setPending(null)}
        onConfirm={confirmRun}
        type="warning"
        title={t('projects.run.approveTitle')}
        message={
          <span data-testid="run-approval">
            {repoNames.size > 1
              ? t('projects.run.approveMessageMany', { count: repoNames.size })
              : t('projects.run.approveMessage', { repo: pending?.[0]?.repo.name || pending?.[0]?.repo.path || '' })}
            {(pending || []).map(({ repo, config: c }) => (
              <span key={`${repo.path}|${c.name}`} className="pj-run-approve-cmd">
                <span className="pj-run-approve-name">{repoNames.size > 1 ? `${repo.name || repo.path} · ${c.name}` : c.name}</span>
                <code>{commandLine(c)}</code>
                {c.cwd && <span className="pj-run-approve-meta">{t('projects.run.approveCwd', { cwd: c.cwd })}</span>}
                {c.env && Object.keys(c.env).length > 0 && (
                  <span className="pj-run-approve-meta">
                    {t('projects.run.approveEnv', { env: Object.entries(c.env).map(([k, v]) => `${k}=${v}`).join(' ') })}
                  </span>
                )}
              </span>
            ))}
          </span>
        }
        confirmText={t('projects.run.approveConfirm')}
        cancelText={t('common.cancel')}
      />
    </section>
  );
}
